import { X } from 'lucide-react';
/**
 * Sub-spec 10 Plan 4 — filter-pill builder UI.
 *
 * Renders a SegmentFilter as a stack of pills. Each pill has a kind label,
 * a kind-appropriate value editor, and an X button. Below the stack, an
 * "+ Add filter" select adds a new pill.
 *
 * Hand-rolls a value editor per `valueKind`:
 *   - date: <input type="date">
 *   - money_minor: <input type="number"> displaying major units, stored as minor
 *   - string_list: comma-separated <input type="text">
 *   - channel_boolean: channel select + allow/deny toggle
 *   - boolean: yes/no select
 */
import { useState } from 'react';
import type { MarketingChannel, PredicateRegistry, SegmentFilter, SegmentPredicate } from './types';

interface Props {
  value: SegmentFilter;
  onChange: (next: SegmentFilter) => void;
  registry: PredicateRegistry | null;
}

function defaultValueForKind(kind: string): SegmentPredicate {
  switch (kind) {
    case 'last_visit_before':
    case 'last_visit_after':
      return { kind: kind as 'last_visit_before', value: '2026-01-01' };
    case 'lifetime_spend_min_minor':
    case 'lifetime_spend_max_minor':
      return { kind: kind as 'lifetime_spend_min_minor', value: 0 };
    case 'pet_species':
      return { kind: 'pet_species', values: ['dog'] };
    case 'marketing_opt_in':
      return { kind: 'marketing_opt_in', channel: 'email', value: true };
    case 'has_active_membership':
    case 'has_active_pack':
      return { kind: kind as 'has_active_membership', value: true };
    default:
      throw new Error(`unknown predicate kind: ${kind}`);
  }
}

export function FilterPillBuilder({ value, onChange, registry }: Props) {
  // selectKey forces a remount of the <select> after each add so the
  // controlled value resets to '' (the placeholder) without a separate
  // useState round-trip — keeps userEvent flows deterministic in tests.
  const [selectKey, setSelectKey] = useState(0);

  function updatePredicate(idx: number, next: SegmentPredicate) {
    const out = { ...value, predicates: value.predicates.slice() };
    out.predicates[idx] = next;
    onChange(out);
  }
  function removePredicate(idx: number) {
    const out = { ...value, predicates: value.predicates.slice() };
    out.predicates.splice(idx, 1);
    onChange(out);
  }
  function addPredicate(kind: string) {
    if (!kind) return;
    const next = defaultValueForKind(kind);
    onChange({ ...value, predicates: [...value.predicates, next] });
    setSelectKey((k) => k + 1);
  }

  if (!registry) {
    return <div className="text-sm text-muted-foreground">Loading predicate registry…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {value.predicates.map((p, idx) => (
          <PredicateRow
            // biome-ignore lint/suspicious/noArrayIndexKey: predicates are ordered by index and have no stable id
            key={idx}
            predicate={p}
            registry={registry}
            onChange={(next) => updatePredicate(idx, next)}
            onRemove={() => removePredicate(idx)}
          />
        ))}
        {value.predicates.length === 0 && (
          <div className="text-sm text-muted-foreground">
            No filters yet — campaign will target every customer in your tenant.
          </div>
        )}
      </div>
      <div className="flex gap-2 items-center">
        <select
          key={selectKey}
          defaultValue=""
          onChange={(e) => addPredicate(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
          aria-label="Add filter"
        >
          <option value="">+ Add filter</option>
          {Object.entries(registry).map(([kind, entry]) => (
            <option key={kind} value={kind}>
              {entry.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface RowProps {
  predicate: SegmentPredicate;
  registry: PredicateRegistry;
  onChange: (next: SegmentPredicate) => void;
  onRemove: () => void;
}

function PredicateRow({ predicate, registry, onChange, onRemove }: RowProps) {
  const entry = registry[predicate.kind];
  const label = entry?.label ?? predicate.kind;
  return (
    <div className="flex flex-wrap gap-2 items-center border rounded px-3 py-2 bg-secondary">
      <span className="text-sm font-medium sm:min-w-[180px]">{label}</span>
      <ValueEditor predicate={predicate} onChange={onChange} />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove filter"
        className="ml-auto p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

function ValueEditor({
  predicate,
  onChange,
}: {
  predicate: SegmentPredicate;
  onChange: (next: SegmentPredicate) => void;
}) {
  if (predicate.kind === 'last_visit_before' || predicate.kind === 'last_visit_after') {
    return (
      <input
        type="date"
        value={predicate.value}
        onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        className="max-w-full border rounded px-2 py-1 text-sm"
        aria-label={predicate.kind}
      />
    );
  }
  if (
    predicate.kind === 'lifetime_spend_min_minor' ||
    predicate.kind === 'lifetime_spend_max_minor'
  ) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground">major</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={(predicate.value / 100).toFixed(2)}
          onChange={(e) => {
            const major = Number(e.target.value);
            onChange({ ...predicate, value: Math.round(major * 100) });
          }}
          className="border rounded px-2 py-1 text-sm num w-28"
          aria-label={predicate.kind}
        />
      </div>
    );
  }
  if (predicate.kind === 'pet_species') {
    return (
      <input
        type="text"
        value={predicate.values.join(', ')}
        onChange={(e) =>
          onChange({
            ...predicate,
            values: e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          })
        }
        placeholder="dog, cat"
        className="max-w-full border rounded px-2 py-1 text-sm"
        aria-label="Pet species values"
      />
    );
  }
  if (predicate.kind === 'marketing_opt_in') {
    return (
      <div className="flex gap-2">
        <select
          value={predicate.channel}
          onChange={(e) => onChange({ ...predicate, channel: e.target.value as MarketingChannel })}
          className="border rounded px-2 py-1 text-sm"
          aria-label="Channel"
        >
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select
          value={predicate.value ? 'allowed' : 'disallowed'}
          onChange={(e) => onChange({ ...predicate, value: e.target.value === 'allowed' })}
          className="border rounded px-2 py-1 text-sm"
          aria-label="Allowed / disallowed"
        >
          <option value="allowed">Allowed</option>
          <option value="disallowed">Disallowed</option>
        </select>
      </div>
    );
  }
  // boolean
  return (
    <select
      value={predicate.value ? 'yes' : 'no'}
      onChange={(e) => onChange({ ...predicate, value: e.target.value === 'yes' })}
      className="border rounded px-2 py-1 text-sm"
      aria-label={predicate.kind}
    >
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}
